import { AGENCY_ROLES } from '../agency.ts';
import { z } from 'zod';
import { BRAND_STATUSES } from '../brand-status.ts';
import { IdParams, IsoDateTime, Ok } from '../common.ts';
import { ROLES } from '../role.ts';
import { defineRoute } from '../route.ts';

/**
 * 계정의 소속 하나 (261010-agency-console · 261013-account-brand-membership). 계정 = 로그인 신원, 소속 = 무엇으로 쓰는가.
 * 브랜드 소속은 여러 개(한 계정이 브랜드 여럿을 관리한다), 운영자 소속은 단독이다.
 * ★ DB 에는 옛 대행사 소속(kind=agency)이 수축 배포 전까지 남아 있지만 **읽지 않는다**
 */
export const MEMBERSHIP_KINDS = ['admin', 'brand'] as const;
export type MembershipKind = (typeof MEMBERSHIP_KINDS)[number];

/** 브랜드 소속의 역할 — 소유자는 사람을 붙이고 떼고 브랜드를 지운다, 관리자는 쓰기만 한다 */
export const BRAND_ROLES = ['owner', 'manager'] as const;
export type BrandRole = (typeof BRAND_ROLES)[number];

export const Membership = z.object({
  id: z.uuid(),
  kind: z.enum(MEMBERSHIP_KINDS),
  brandId: z.uuid().nullable(),
  brandName: z.string().nullable(),
  /** 브랜드 소속의 역할. 운영자 소속은 null */
  role: z.enum(BRAND_ROLES).nullable(),
  /** 그 브랜드의 승인 상태. 운영자 소속은 null */
  status: z.enum(BRAND_STATUSES).nullable(),
});
export type Membership = z.infer<typeof Membership>;

export const Password = z.string().min(8, '비밀번호는 8자 이상').max(200);

export const Me = z.object({
  id: z.uuid(),
  email: z.string(),
  role: z.enum(ROLES),
  /** 승인된 브랜드가 한 곳뿐이면 그 브랜드. 여럿이면 null — 화면이 고른다 */
  brandId: z.uuid().nullable(),
  mustChangePassword: z.boolean(),
  /**
   * 사용자 영역의 상태 — 승인된 브랜드가 있으면 active, 결제 대기만 있으면 confirmed, 승인 대기만 있으면 pending.
   * 운영자는 null. `.default` 를 두는 이유 — 배포 순간 열려 있던 구버전 탭이 이 필드 없이 파싱해도 깨지지 않게
   */
  /** 브랜드 상태 + `none`(브랜드가 하나도 없다 — 모두 지워졌다. 계정·브랜드 추가만 열린다, 261022-brand-delete-is-contract-end) */
  brandStatus: z
    .enum([...BRAND_STATUSES, 'none'])
    .nullable()
    .default(null),
  /** 대리 로그인 중이면 연 운영자. 이 탭의 신원은 대상 계정이다 (260917-realm-sessions-impersonation) */
  impersonator: z.object({ email: z.string() }).nullable().default(null),
  /** 이 계정의 소속 전부 — 브랜드 선택이 본다. 운영자 소속은 단독이라 브랜드 소속과 함께 오지 않는다 */
  memberships: z.array(Membership).default([]),
  /**
   * 대행사 안의 위치(261025-agency-white-label) — client 는 결제 화면을 보지 않는다(서버도 결제 계약을 막는다).
   * 자기 위치만 싣는다 — 대행사 id·다른 계정의 위치는 싣지 않는다
   */
  agencyRole: z.enum(AGENCY_ROLES).nullable().default(null),
});
export type Me = z.infer<typeof Me>;

export const login = defineRoute({
  method: 'post',
  path: '/api/auth/login',
  access: 'public',
  summary: '이메일/비밀번호 로그인 — 세션 쿠키를 심는다',
  body: z.object({ email: z.string().min(3).max(320), password: z.string().min(1).max(200) }),
  response: z.object({ me: Me }),
});

export const logout = defineRoute({
  method: 'post',
  path: '/api/auth/logout',
  access: 'session',
  summary: '현재 세션을 DB 에서 폐기한다',
  response: Ok,
});

export const me = defineRoute({
  method: 'get',
  path: '/api/auth/me',
  access: 'session',
  summary: '내 계정',
  response: Me,
});

export const changePassword = defineRoute({
  method: 'post',
  path: '/api/auth/password',
  access: 'session',
  summary: '비밀번호 변경 — 다른 세션을 모두 폐기한다',
  body: z.object({ currentPassword: z.string().min(1).max(200), newPassword: Password }),
  response: Ok,
});

export const UserRow = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  /** 회사명(선택) — 누구인지 알아보는 용도 */
  companyName: z.string().nullable(),
  /** 살아 있는 소속 — 없으면 어디에도 로그인할 수 없는 계정이다 */
  memberships: z.array(Membership),
  mustChangePassword: z.boolean(),
  lockedUntil: IsoDateTime.nullable(),
  /** (선택) 마케팅 정보 수신 동의 시각. null = 미동의 — 광고성 정보는 이 값이 있는 계정에만 보낸다 */
  marketingAgreedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  /** 마지막으로 **본인이** 로그인한 시각 — 운영자 대리 로그인은 세지 않는다. 한 번도 없으면 null */
  lastLoginAt: IsoDateTime.nullable(),
  /** 마지막 활동(본인 세션 중 가장 늦은 것). 갱신은 몇 분 단위로 묶여서 정확한 분이 아니다 */
  lastSeenAt: IsoDateTime.nullable(),
  /** 최근 30일 본인 로그인 횟수 */
  loginCount30d: z.number().int(),
});

/**
 * 로그인 한 번 = 세션 한 행이다(user_session — 지우지 않고 남는다). 운영자 화면 전용.
 * - active: 아직 살아 있다 / loggedOut: 로그아웃·비밀번호 변경·탈퇴 등으로 끝났다 / expired: 시간이 다 됐다(절대·유휴)
 * - impersonatedBy: 운영자가 대리 로그인한 세션이면 그 운영자 이메일
 */
export const UserLogin = z.object({
  at: IsoDateTime,
  lastSeenAt: IsoDateTime,
  endedAt: IsoDateTime.nullable(),
  state: z.enum(['active', 'loggedOut', 'expired']),
  /**
   * 어느 화면으로 들어왔나 — 운영자(admin)·사용자(app). 영역을 나누기 전의 세션은 null.
   * ★ 문자열로 받는다 — 지운 대행사 영역(agency, 261013-account-brand-membership)의 옛 세션 행이 남아 있다. 좁히면 기록 창이 500 이다
   */
  realm: z.string().nullable(),
  impersonatedBy: z.string().nullable(),
});
export type UserLogin = z.infer<typeof UserLogin>;

/** 한 계정의 로그인 기록 — 최근 100건 */
export const listUserLogins = defineRoute({
  method: 'get',
  path: '/api/users/{id}/logins',
  access: 'admin',
  summary: '계정의 로그인 기록',
  params: IdParams,
  response: z.object({ logins: z.array(UserLogin) }),
});

export const listUsers = defineRoute({
  method: 'get',
  path: '/api/users',
  access: 'admin',
  summary: '계정 목록',
  response: z.object({ users: z.array(UserRow) }),
});

export const createUser = defineRoute({
  method: 'post',
  path: '/api/users',
  access: 'admin',
  summary:
    '계정 발급 — 첫 소속과 함께. 임시 비밀번호, 첫 로그인에 변경 강제. 이미 있는 이메일이면 409(소속 추가를 쓴다)',
  body: z
    .object({
      email: z.email(),
      role: z.enum(ROLES),
      /** member 면 첫 브랜드(필수), admin 이면 null */
      brandId: z.uuid().nullable(),
      /** member 의 첫 소속 역할 */
      brandRole: z.enum(BRAND_ROLES).default('owner'),
      companyName: z.string().trim().max(120).nullable().default(null),
      temporaryPassword: Password,
    })
    .strict(),
  response: z.object({ user: UserRow }),
});

export const resetUserPassword = defineRoute({
  method: 'post',
  path: '/api/users/{id}/reset-password',
  access: 'admin',
  summary: '임시 비밀번호 재발급 — 그 사용자의 세션을 모두 폐기한다',
  params: IdParams,
  body: z.object({ temporaryPassword: Password }).strict(),
  response: Ok,
});

/**
 * 대리 로그인 — 대상 계정으로 보는 **탭 한정 토큰**을 준다. 쿠키를 심지 않는다: 운영자 세션도, 같은 브라우저의
 * 고객 세션도 바뀌지 않는다. 토큰은 연 운영자 세션보다 오래 살지 않는다
 */
export const startImpersonation = defineRoute({
  method: 'post',
  path: '/api/users/{id}/impersonate',
  access: 'admin',
  summary: '대리 로그인 — 대상 계정의 사용자 화면(/app)으로 보는 탭 토큰(2시간, 운영자 세션에 묶임)',
  params: IdParams,
  response: z.object({
    token: z.string(),
    expiresInMs: z.number().int(),
    target: z.object({ email: z.string() }),
  }),
});

export const addMembership = defineRoute({
  method: 'post',
  path: '/api/users/{id}/memberships',
  access: 'admin',
  summary: '계정에 브랜드 소속 추가(역할과 함께). 운영자 계정에는 붙일 수 없다',
  params: IdParams,
  body: z.object({ kind: z.literal('brand'), brandId: z.uuid(), role: z.enum(BRAND_ROLES).default('owner') }).strict(),
  response: z.object({ user: UserRow }),
});

export const updateMembership = defineRoute({
  method: 'patch',
  path: '/api/memberships/{id}',
  access: 'admin',
  summary: '브랜드 소속의 역할 바꾸기 — 마지막 소유자를 관리자로 내리면 409',
  params: IdParams,
  body: z.object({ role: z.enum(BRAND_ROLES) }).strict(),
  response: Ok,
});

export const removeMembership = defineRoute({
  method: 'delete',
  path: '/api/memberships/{id}',
  access: 'admin',
  summary: '소속 삭제 — 그 영역의 세션이 끝난다. 계정은 남는다. 마지막 소유자면 409(다른 소속자를 먼저 소유자로)',
  params: IdParams,
  response: Ok,
});

export const deleteUser = defineRoute({
  method: 'delete',
  path: '/api/users/{id}',
  access: 'admin',
  summary: '계정 비활성화(soft delete) — 세션을 모두 폐기한다',
  params: IdParams,
  response: Ok,
});
